import React, { useState, useEffect, useRef, useMemo } from "react";

import ltcLogo from "../../assets/ltc_icon.png";
import bchLogo from "../../assets/bch_icon.png";
import dogeLogo from "../../assets/doge_icon.png";
import dashLogo from "../../assets/dash_logo.png";
import polLogo from "../../assets/pol_logo.png";
import zecLogo from "../../assets/zec_logo.png";

import {
  Box,
  Text,
  Image,
  Button,
  Link,
  VStack,
  HStack,
  Badge,
  Tooltip,
  useToast,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ExternalLinkIcon, CopyIcon, CheckIcon } from "@chakra-ui/icons";

// ─── Constants ───────────────────────────────────────────────
const LOGO = {
  ltclogo: ltcLogo,
  bchlogo: bchLogo,
  dogelogo: dogeLogo,
  dashlogo: dashLogo,
  pollogo: polLogo,
  zeclogo: zecLogo,
};

const COLORS = {
  LTC: { color: "#345D9D", gradient: "linear(to-br, #345D9D, #5A95D8)", shadow: "rgba(52, 93, 157, 0.4)" },
  BCH: { color: "#8DC351", gradient: "linear(to-br, #8DC351, #B8E986)", shadow: "rgba(141, 195, 81, 0.4)" },
  DOGE: { color: "#C3A634", gradient: "linear(to-br, #C3A634, #F0D365)", shadow: "rgba(195, 166, 52, 0.4)" },
  DASH: { color: "#008DE4", gradient: "linear(to-br, #008DE4, #40B5F5)", shadow: "rgba(0, 141, 228, 0.4)" },
  POL: { color: "#8247E5", gradient: "linear(to-br, #8247E5, #A87EF0)", shadow: "rgba(130, 71, 229, 0.4)" },
  ZEC: { color: "#ECB244", gradient: "linear(to-br, #ECB244, #F5D078)", shadow: "rgba(236, 178, 68, 0.4)" },
  DEFAULT: { color: "#38B2AC", gradient: "linear(to-br, #38B2AC, #4FD1C5)", shadow: "rgba(56, 178, 172, 0.4)" },
};

const SHIMMER_STYLE = (
  <style>{`
    @keyframes wallet-shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    @keyframes wallet-glow {
      0%, 100% { opacity: 0.6; }
      50% { opacity: 1; }
    }
  `}</style>
);

// ─── Reusable shimmer placeholder ────────────────────────────
const ShimmerBox = ({ h, w = "full", borderRadius = "md", delay = 0, ...rest }) => (
  <Box
    position="relative"
    overflow="hidden"
    borderRadius={borderRadius}
    bg="whiteAlpha.100"
    height={h}
    width={w}
    {...rest}
  >
    <Box
      position="absolute"
      inset={0}
      background="linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)"
      animation="wallet-shimmer 2s infinite"
      style={{ animationDelay: `${delay}s` }}
    />
  </Box>
);

// ─── 24h Change Badge (used in both mobile & desktop) ────────
const Change24h = ({ value, isMobile }) => {
  if (!value || Number(value) === 0) return null;
  const num = Number(value);
  const isUp = num > 0;

  if (isMobile) {
    return (
      <HStack spacing={1} justify="center" pt={0.5}>
        <Text
          fontSize="8px"
          color={isUp ? "green.400" : "red.400"}
          fontWeight="bold"
        >
          {isUp ? "↑" : "↓"} {Math.abs(num).toFixed(2)}%
        </Text>
        <Text fontSize="7px" color="whiteAlpha.400">24h</Text>
      </HStack>
    );
  }

  return (
    <Badge
      colorScheme={isUp ? "green" : "red"}
      fontSize={{ sm: "9px", md: "8px" }}
      px={1.5}
      borderRadius="md"
      fontWeight="bold"
    >
      {isUp ? "↑" : "↓"} {Math.abs(num).toFixed(2)}%
    </Badge>
  );
};

// ─── Main Component ──────────────────────────────────────────
export default function WalletCard({ address, logo, wallet, loading, error }) {
  const toast = useToast();
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [balanceRevealed, setBalanceRevealed] = useState(false);

  const isMobile = useBreakpointValue({ base: true, md: false });
  const cardMaxW = useBreakpointValue({ base: "160px", sm: "280px", md: "280px" });
  const cardPadding = useBreakpointValue({ base: 1.5, sm: 3, md: 3.5 });
  const logoSize = useBreakpointValue({ base: "26px", sm: "40px", md: "42px" });

  const color = wallet?.symbol && COLORS[wallet.symbol] ? COLORS[wallet.symbol] : COLORS.DEFAULT;
  const logoPath = LOGO[logo];

  const walletData = useMemo(() => {
    if (!wallet) return { amount: 0, price: 0, totalUsd: 0 };
    const amount = Number(wallet.amount || 0);
    const price = Number(wallet.price || 0);
    return { amount, price, totalUsd: amount * price };
  }, [wallet]);

  useEffect(() => {
    if (!loading && wallet) {
      const t = setTimeout(() => setBalanceRevealed(true), 200);
      return () => clearTimeout(t);
    }
  }, [loading, wallet]);

  // ─── 3D tilt (desktop only) ─────────────────────────────────
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const raf = useRef(null);

  const handleMouseMove = (e) => {
    if (isMobile || !cardRef.current) return;
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      const rect = cardRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 8;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * -8;
      setTilt({ x, y });
    });
  };

  // Cleanup raf on unmount
  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const copy = () => {
    navigator.clipboard.writeText(address).then(() => {
      setCopied(true);
      toast({
        title: "Copied!",
        status: "success",
        position: "top",
        duration: 1500,
      });
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const truncate = (a) => (a ? `${a.slice(0, 8)}...${a.slice(-8)}` : "");

  const explorer = wallet?.explorer
    ? `https://blockchair.com/${wallet.explorer}/address/${address}`
    : "#";

  // ─── Shared card shell ──────────────────────────────────────
  const cardShell = {
    borderRadius: { base: "lg", md: "xl" },
    p: cardPadding,
    bg: "linear-gradient(135deg, rgba(26,32,44,0.95), rgba(45,55,72,0.95))",
    border: "1px solid",
    borderColor: "whiteAlpha.200",
    maxW: cardMaxW,
    mx: "auto",
    overflow: "hidden",
    position: "relative",
  };

  // ─── Loading state ──────────────────────────────────────────
  if (loading) {
    return (
      <Box {...cardShell}>
        {SHIMMER_STYLE}
        <VStack spacing={2}>
          <HStack w="full" justify="space-between">
            <HStack spacing={2}>
              <ShimmerBox h={logoSize} w={logoSize} borderRadius="lg" delay={0} />
              <VStack spacing={1} align="start">
                <ShimmerBox h="14px" w="45px" delay={0.1} />
                <ShimmerBox h="8px" w="35px" delay={0.2} />
              </VStack>
            </HStack>
            <ShimmerBox h="18px" w="45px" borderRadius="full" delay={0.3} />
          </HStack>
          <ShimmerBox h="55px" borderRadius="lg" delay={0.4} />
          <ShimmerBox h="40px" delay={0.5} />
          <HStack w="full" spacing={2}>
            <ShimmerBox h="36px" flex={1} borderRadius="lg" delay={0.6} />
            <ShimmerBox h="36px" flex={1} borderRadius="lg" delay={0.7} />
          </HStack>
        </VStack>
      </Box>
    );
  }

  // ─── Error state ────────────────────────────────────────────
  if (error) {
    return (
      <Box {...cardShell} borderColor="red.500">
        <VStack spacing={2} py={3}>
          <Text fontSize="xl">⚠️</Text>
          <Text color="red.300" fontSize="xs" textAlign="center" fontWeight="semibold">
            {error}
          </Text>
        </VStack>
      </Box>
    );
  }

  // ─── Main render ────────────────────────────────────────────
  return (
    <Box
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => !isMobile && setTilt({ x: 0, y: 0 })}
      style={{
        transform: isMobile
          ? "none"
          : `perspective(1000px) rotateX(${tilt.y}deg) rotateY(${tilt.x}deg)`,
      }}
      transition="transform 0.15s ease-out"
      {...cardShell}
      bg="linear-gradient(135deg, rgba(26,32,44,0.98), rgba(45,55,72,0.98))"
      boxShadow={`0 ${isMobile ? "6" : "12"}px ${isMobile ? "18" : "35"}px rgba(0,0,0,0.5), 0 0 ${isMobile ? "12" : "25"}px ${color.shadow}`}
      _hover={{
        borderColor: "whiteAlpha.300",
        boxShadow: `0 ${isMobile ? "8" : "16"}px ${isMobile ? "22" : "42"}px rgba(0,0,0,0.6), 0 0 ${isMobile ? "16" : "32"}px ${color.shadow}`,
      }}
    >
      {SHIMMER_STYLE}

      {/* Top gradient line */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        height="2px"
        bgGradient={color.gradient}
        animation="wallet-glow 3s ease-in-out infinite"
      />

      {/* Background glow (desktop only) */}
      {!isMobile && (
        <Box
          position="absolute"
          inset="-2px"
          borderRadius="xl"
          bgGradient={color.gradient}
          opacity={0.08}
          filter="blur(18px)"
          zIndex={0}
          pointerEvents="none"
        />
      )}

      <VStack spacing={{ base: 1.5, sm: 2.5, md: 2 }} position="relative" zIndex={2}>
        {/* ─── Header ──────────────────────────────────────────── */}
        <HStack w="full" justify="space-between">
          <HStack spacing={{ base: 1.5, sm: 2, md: 2.5 }}>
            <Box
              bgGradient={color.gradient}
              boxSize={logoSize}
              borderRadius={{ base: "md", sm: "lg" }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow={`0 0 ${isMobile ? "10" : "16"}px ${color.shadow}`}
            >
              <Image src={logoPath} alt="logo" boxSize="70%" />
            </Box>
            <VStack spacing={0} align="start">
              <Text
                fontSize={{ base: "xs", sm: "lg", md: "lg" }}
                fontWeight="bold"
                color="white"
              >
                {wallet?.symbol || "..."}
              </Text>
              <Text
                fontSize={{ base: "8px", sm: "10px", md: "9px" }}
                color="whiteAlpha.500"
                fontWeight="semibold"
                letterSpacing="wide"
              >
                WALLET
              </Text>
            </VStack>
          </HStack>

          <Badge
            bgGradient="linear(to-r, green.400, green.500)"
            color="white"
            borderRadius="full"
            px={{ base: 1, sm: 2, md: 1.5 }}
            py={{ base: 0, sm: 0.5 }}
            fontSize={{ base: "8px", sm: "10px", md: "9px" }}
            fontWeight="bold"
            boxShadow="0 0 8px rgba(72, 187, 120, 0.4)"
          >
            ● LIVE
          </Badge>
        </HStack>

        {/* ─── Balance ─────────────────────────────────────────── */}
        <Box
          bg="whiteAlpha.100"
          px={{ base: 2, sm: 3 }}
          py={{ base: 1.5, sm: 2.5 }}
          borderRadius={{ base: "md", sm: "lg" }}
          w="full"
          border="1px solid"
          borderColor="whiteAlpha.200"
          backdropFilter="blur(10px)"
          opacity={balanceRevealed ? 1 : 0}
          transform={balanceRevealed ? "translateY(0)" : "translateY(10px)"}
          transition="all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)"
        >
          <Text
            fontSize={{ base: "7px", sm: "10px", md: "9px" }}
            color="whiteAlpha.500"
            fontWeight="bold"
            letterSpacing="wide"
            mb={{ base: 0.5, sm: 1 }}
          >
            BALANCE
          </Text>

          {/* Mobile: stacked */}
          <VStack spacing={0.5} align="stretch" display={{ base: "flex", sm: "none" }}>
            <HStack spacing={0.5} justify="center">
              <Text fontSize="sm" fontWeight="black" color="white" lineHeight="1">
                {walletData.amount.toFixed(4)}
              </Text>
              <Text fontSize="8px" color="whiteAlpha.600" fontWeight="semibold">
                {wallet?.symbol}
              </Text>
            </HStack>
            <HStack spacing={1} justify="center" align="baseline">
              <Text fontSize="xs" fontWeight="bold" color="green.300" lineHeight="1">
                ${walletData.totalUsd.toFixed(2)}
              </Text>
              <Text fontSize="7px" color="whiteAlpha.500">
                @ ${walletData.price.toFixed(2)}
              </Text>
            </HStack>
            <Change24h value={wallet?.change24h} isMobile />
          </VStack>

          {/* Desktop: horizontal */}
          <HStack
            justify="space-between"
            align="baseline"
            display={{ base: "none", sm: "flex" }}
          >
            <HStack spacing={0.5}>
              <Text
                fontSize={{ sm: "xl", md: "xl" }}
                fontWeight="black"
                color="white"
                lineHeight="1"
              >
                {walletData.amount.toFixed(4)}
              </Text>
              <Text
                fontSize={{ sm: "xs", md: "2xs" }}
                color="whiteAlpha.600"
                fontWeight="semibold"
              >
                {wallet?.symbol}
              </Text>
            </HStack>
            <VStack spacing={0} align="end">
              <Text
                fontSize={{ sm: "xl", md: "xl" }}
                fontWeight="bold"
                color="green.300"
                lineHeight="1"
              >
                ${walletData.totalUsd.toFixed(2)}
              </Text>
              <Text fontSize={{ sm: "10px", md: "9px" }} color="whiteAlpha.500">
                @ ${walletData.price.toFixed(2)}
              </Text>
              <Change24h value={wallet?.change24h} isMobile={false} />
            </VStack>
          </HStack>
        </Box>

        {/* ─── Address ─────────────────────────────────────────── */}
        <Tooltip label={address} hasArrow bg="gray.700" color="white" fontSize="xs">
          <Box
            bg="whiteAlpha.100"
            px={{ base: 2, sm: 3, md: 2.5 }}
            py={{ base: 1, sm: 2, md: 1.5 }}
            borderRadius="md"
            border="1px solid"
            borderColor="whiteAlpha.200"
            w="full"
            textAlign="center"
            backdropFilter="blur(10px)"
            cursor="pointer"
            _hover={{ bg: "whiteAlpha.150", borderColor: "whiteAlpha.300" }}
          >
            <Text
              fontSize={{ base: "7px", sm: "10px", md: "9px" }}
              color="whiteAlpha.400"
              mb={{ base: 0, sm: 0.5 }}
              fontWeight="bold"
              letterSpacing="wide"
            >
              ADDRESS
            </Text>
            <Text
              fontFamily="monospace"
              fontSize={{ base: "9px", sm: "xs" }}
              color="whiteAlpha.800"
              fontWeight="semibold"
              lineHeight="1.2"
            >
              {truncate(address)}
            </Text>
          </Box>
        </Tooltip>

        {/* ─── Buttons ─────────────────────────────────────────── */}
        <HStack w="full" spacing={{ base: 1, sm: 2 }}>
          <Button
            flex={1}
            bgGradient={color.gradient}
            color="white"
            fontWeight="bold"
            size={{ base: "xs", sm: "md", md: "sm" }}
            fontSize={{ base: "8px", sm: "sm", md: "xs" }}
            leftIcon={
              copied
                ? <CheckIcon boxSize={{ base: 2, sm: 3.5, md: 3 }} />
                : <CopyIcon boxSize={{ base: 2, sm: 3.5, md: 3 }} />
            }
            onClick={copy}
            borderRadius={{ base: "md", sm: "lg" }}
            boxShadow={`0 3px 12px ${color.shadow}`}
            _hover={{ transform: "translateY(-2px)", boxShadow: `0 5px 16px ${color.shadow}` }}
            _active={{ transform: "translateY(0)" }}
            isDisabled={copied}
            h={{ base: "30px", sm: "40px", md: "36px" }}
            minW={{ base: "70px", sm: "auto" }}
            px={{ base: 2, sm: 4 }}
          >
            {copied ? "COPIED" : "COPY"}
          </Button>

          {explorer !== "#" && (
            <Button
              flex={1}
              color={color.color}
              variant="outline"
              borderColor={color.color}
              size={{ base: "xs", sm: "md", md: "sm" }}
              fontSize={{ base: "8px", sm: "sm", md: "xs" }}
              as={Link}
              href={explorer}
              isExternal
              rightIcon={<ExternalLinkIcon boxSize={{ base: 2, sm: 3.5, md: 3 }} />}
              borderRadius={{ base: "md", sm: "lg" }}
              fontWeight="bold"
              _hover={{ bg: `${color.color}22`, transform: "translateY(-2px)" }}
              _active={{ transform: "translateY(0)" }}
              h={{ base: "30px", sm: "40px", md: "36px" }}
              minW={{ base: "70px", sm: "auto" }}
              px={{ base: 2, sm: 4 }}
            >
              <Text as="span" display={{ base: "none", sm: "inline" }}>EXPLORER</Text>
              <Text as="span" display={{ base: "inline", sm: "none" }}>VIEW</Text>
            </Button>
          )}
        </HStack>
      </VStack>
    </Box>
  );
}